import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FiMapPin, FiChevronRight, FiAlertCircle } from 'react-icons/fi';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';
import PortalAccessGate from '../components/PortalAccessGate';

// Staff viewing a hub send their login token to skip the public last-4 gate.
async function hubConfig() {
  try {
    const t = await getAuthToken();
    return t ? { withCredentials: true, headers: { Authorization: `Bearer ${t}` } } : { withCredentials: true };
  } catch (e) { return { withCredentials: true }; }
}

// One link for a repeat client: lists all their projects, each opening its own portal.
export default function ClientHub() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ACCESS_KEY = `client_access_${token}`;
  const [accessToken, setAccessToken] = useState(() => {
    try { return window.localStorage.getItem(ACCESS_KEY) || ''; } catch (e) { return ''; }
  });

  const load = async (at) => {
    const q = at ? `?access=${encodeURIComponent(at)}` : '';
    const res = await axios.get(`${API_URL}/public/client-portal/${token}${q}`, await hubConfig());
    setData(res.data);
    return res.data;
  };

  useEffect(() => {
    (async () => {
      try { await load(accessToken); }
      catch (err) { setError(err.response?.status === 404 ? 'This link is no longer active.' : 'Failed to load.'); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleVerified = async (newToken) => {
    try { window.localStorage.setItem(ACCESS_KEY, newToken); } catch (e) {}
    setAccessToken(newToken);
    setLoading(true);
    try { await load(newToken); } catch (e) { /* ignore */ }
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 24 }}>Loading...</div>
    );
  }

  if (data && data.needs_access) {
    return (
      <PortalAccessGate
        company={data.company}
        subtitle={data.client_name ? `${data.client_name} — all projects` : 'All projects'}
        verifyPath={`/public/client-portal/${token}/access`}
        onVerified={handleVerified}
      />
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 40, textAlign: 'center', maxWidth: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <FiAlertCircle size={48} color="#dc2626" />
          <h2 style={{ margin: '16px 0 8px', color: '#111827' }}>Not Found</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>{error || 'This link may have expired.'}</p>
        </div>
      </div>
    );
  }

  const { company, client_name, jobs = [], collaborator_jobs = [] } = data;

  const jobCard = (j) => (
    <Link key={j.portal_token} to={`/portal/${j.portal_token}`}
      style={{ textDecoration: 'none', color: 'inherit' }}>
      <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {j.job_number}
            {j.phase_name && (
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', background: '#f5f3ff', color: '#6d28d9', padding: '2px 8px', borderRadius: 8 }}>
                {j.phase_name}
              </span>
            )}
          </div>
          {j.property_address && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
              <FiMapPin size={12} /> {j.property_address}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {j.status && (
              <span style={{ fontSize: 11, fontWeight: 600, background: '#eff6ff', color: '#1d4ed8', padding: '3px 10px', borderRadius: 10 }}>
                {j.status}
              </span>
            )}
            {j.relationship === 'collaborator' && j.role && (
              <span style={{ fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: '#475569', padding: '3px 10px', borderRadius: 10 }}>
                {j.role}
              </span>
            )}
          </div>
        </div>
        <FiChevronRight size={22} color="#9ca3af" />
      </div>
    </Link>
  );

  const sectionLabel = (text) => (
    <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, margin: '22px 0 8px' }}>
      {text}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      <div style={{ backgroundColor: '#1e293b', color: 'white', padding: '20px 0' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          {company?.logo && (
            <img src={`${API_BASE_URL}${company.logo}`} alt="Logo" style={{ height: 40, borderRadius: 6 }} onError={(e) => (e.target.style.display = 'none')} />
          )}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{company?.name || 'North Georgia Surveying'}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Client Project Portal</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: 20 }}>
        <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 8 }}>
          {client_name ? `Welcome, ${client_name}` : 'Your Projects'}
        </div>
        <div style={{ fontSize: 15, color: '#374151', marginBottom: 16 }}>
          Select a project to view its status, photos, and documents.
        </div>

        {jobs.length === 0 && collaborator_jobs.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 16, padding: 32, textAlign: 'center', color: '#6b7280', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            No active projects to show right now.
          </div>
        ) : (
          <>
            {jobs.length > 0 && (
              <>
                {collaborator_jobs.length > 0 && sectionLabel('Your Projects')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {jobs.map(jobCard)}
                </div>
              </>
            )}

            {collaborator_jobs.length > 0 && (
              <>
                {sectionLabel("Projects You're Working On")}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {collaborator_jobs.map(jobCard)}
                </div>
              </>
            )}
          </>
        )}

        {company?.phone && (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 24 }}>
            Questions? Call us at {company.phone}.
          </div>
        )}
      </div>
    </div>
  );
}
